import { Disclosure } from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import { Link } from "react-router-dom";
import {
  TERMS_EFFECTIVE_DATE,
  TERMS_PREAMBLE,
  TERMS_SECTIONS,
} from "./termsContent";

const SUPPORT_PHONE = "+1 (310) 564-7495";
const SUPPORT_PHONE_LINK = "tel:+13105647495";
const SUPPORT_EMAIL = "support@kono.ai";
const SUPPORT_EMAIL_LINK = "mailto:support@kono.ai";

const PageHeading = ({ children }: { children: React.ReactNode }) => (
  <h1 className="text-3xl font-bold tracking-tight text-[#00062e] sm:text-4xl">
    {children}
  </h1>
);

/**
 * The team section is off the page while the bios are reworked; the cards and
 * data below stay so flipping this back on republishes it as it was.
 */
const HIDE_TEAM_SECTION = true;

/**
 * Leadership cards for the About page: photo, a role-defining phrase (the
 * hook), and the experience line that backs it up. Ordered CEO / CTO —
 * metals-industry depth and AI engineering — because that combination *is*
 * the investment thesis.
 */
const TEAM: {
  name: string;
  role: string;
  photo: string;
  phrase: string;
  bio: string;
}[] = [
  {
    name: "Michael Resl",
    role: "Chief Executive Officer",
    photo: "/team/michael-resl-2026.jpg",
    phrase:
      "“Metals is a trust business. We keep the handshake — and remove the friction around it.”",
    bio: "Decades in high-performance alloys, including a C-level career with California Metals spanning sourcing, pricing, and aerospace-grade supply — now leading Kono.",
  },
  {
    name: "Jose Adalberto Teran",
    role: "Chief Technology Officer",
    photo: "/team/jose-adalberto-teran.png",
    phrase:
      "“We put AI to work on every step — search, pricing, logistics — so buyers get answers in seconds, not days.”",
    bio: "25+ years building complex systems and online platforms, and designing and deploying production Artificial Intelligence systems.",
  },
];

/** Proof points buyers (and investors) can verify — no vague superlatives. */
const TRUST_POINTS: { stat: string; label: string }[] = [
  { stat: "AS9120", label: "aerospace-grade quality standard supply chain" },
  {
    stat: "Largest",
    label: "online high-performance alloy inventory in North America",
  },
  { stat: "Instant", label: "finding and sourcing of inventory" },
  { stat: "Nationwide", label: "we deliver anywhere in the US" },
  { stat: "DFARS", label: "the majority of our inventory is DFARS compliant" },
];

export const AboutPage = () => {
  return (
    <div className="pb-16">
      {/* Hero — same light, quiet style as the rest of the site: white
          background, navy headline, teal accents. */}
      <div className="mx-auto max-w-3xl px-4 pt-10 text-center sm:pt-14">
        <p className="text-sm font-bold uppercase tracking-widest text-[#009499]">
          About Kono
        </p>
        <h1 className="mt-4 text-3xl font-bold tracking-tight text-[#00062e] sm:text-5xl">
          The intelligent platform for high-performance alloys
        </h1>
        <p className="mx-auto mt-5 max-w-2xl text-lg leading-relaxed text-gray-500">
          Sourcing Inconel, titanium, and aerospace-grade metals has meant
          phone calls, faxed quotes, and days of waiting. We are rebuilding
          that experience end to end — instant, transparent, and reliable.
        </p>
      </div>

      {/* Trust strip */}
      <div className="mx-auto mt-10 grid max-w-6xl grid-cols-2 gap-4 lg:grid-cols-5">
        {TRUST_POINTS.map(({ stat, label }) => (
          <div
            key={stat}
            className="rounded-xl border border-gray-100 bg-white p-5 text-center shadow-sm"
          >
            <div className="text-2xl font-bold text-[#00062e]">{stat}</div>
            <div className="mt-1 text-sm leading-snug text-gray-500">
              {label}
            </div>
          </div>
        ))}
      </div>

      {/* Mission */}
      <div className="mx-auto mt-16 max-w-3xl text-center">
        <h2 className="text-2xl font-bold tracking-tight text-[#00062e] sm:text-3xl">
          Why buyers trust Kono
        </h2>
        <div className="mt-5 space-y-4 text-lg leading-relaxed text-gray-700">
          <p>
            We back every order with the{" "}
            <span className="font-semibold text-[#00062e]">Kono Guarantee</span>{" "}
            of amazing service, and with one of the largest online inventories
            of high-performance alloys in North America. Instantly compare
            pricing and purchase, with certifications and mill documentation
            handled the way aerospace buyers expect.
          </p>
          <p>
            Artificial Intelligence is embedded in every task on our platform —
            understanding your search in plain language, reading your RFQs,
            pricing, and logistics. It is the first time AI models and agentic
            workflows have been put to work for the metals market at this
            depth, and we are only getting started.
          </p>
        </div>
      </div>

      {/* Leadership — hidden for now while the team bios are being reworked.
          Flip HIDE_TEAM_SECTION to false to publish it again. */}
      {!HIDE_TEAM_SECTION && (
      <div className="mx-auto mt-16 max-w-5xl">
        <h2 className="text-center text-2xl font-bold tracking-tight text-[#00062e] sm:text-3xl">
          The team behind Kono
        </h2>
        <p className="mx-auto mt-3 max-w-2xl text-center text-lg text-gray-500">
          Decades in the metals industry and hands-on AI engineering — under
          one roof.
        </p>
        <div className="mt-10 grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
          {TEAM.map(({ name, role, photo, phrase, bio }) => (
            <div
              key={name}
              className="flex flex-col overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm"
            >
              <img
                src={photo}
                alt={`${name}, ${role} of Kono`}
                className="aspect-[3/4] w-full object-cover object-top"
              />
              <div className="flex flex-1 flex-col p-6">
                <h3 className="text-lg font-bold text-[#00062e]">{name}</h3>
                <p className="text-sm font-semibold uppercase tracking-wide text-[#009499]">
                  {role}
                </p>
                <p className="mt-4 text-base font-medium italic leading-relaxed text-gray-800">
                  {phrase}
                </p>
                <p className="mt-4 text-sm leading-relaxed text-gray-500">
                  {bio}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
      )}

      {/* The problem Kono replaces — written to the buyer, styled to stop
          the scroll: big hook questions, one highlighted turning point, and
          the CEO's word on it. */}
      <div className="mx-auto mt-20 max-w-4xl px-4">
        <p className="text-center text-sm font-bold uppercase tracking-widest text-[#009499]">
          Sound familiar?
        </p>
        <div className="mt-6 space-y-5 text-center">
          <h2 className="text-2xl font-bold tracking-tight text-[#00062e] sm:whitespace-nowrap sm:text-3xl">
            How many phone calls does it take you to price a bar of 625?
          </h2>
          <p className="text-lg font-semibold leading-snug text-[#00062e] sm:text-2xl">
            How much emailing around to find the 3&quot; 718 tube to AMS 5590
            that nobody seems to have?
          </p>
          <p className="text-lg font-semibold leading-snug text-[#00062e] sm:text-2xl">
            Ever end up on ThomasNet? Google? Ever had to turn down work
            because you couldn&apos;t find the material in time?
          </p>
        </div>

        <p className="mx-auto mt-10 max-w-3xl text-lg leading-relaxed text-gray-700">
          You know the sequence. You&apos;re bidding against six or eight other
          shops. You call four distributors. Two call back. One has it but
          won&apos;t quote until tomorrow. The price that lands seems high, the
          lead time is worse, and you have no way to know who else is holding
          what you need. You add your labor, send the quote, and ultimately
          don&apos;t win the job.
        </p>

        <p className="mt-10 text-center text-2xl font-bold tracking-tight text-[#009499] sm:text-3xl">
          That is the process we built Kono to replace.
        </p>

        <div className="mt-10 space-y-4 rounded-2xl bg-slate-50 p-8 text-lg leading-relaxed text-gray-700 sm:p-10">
          <p>
            Kono is the spot purchasing platform for high-performance alloys.
            Today we list the largest online Inconel and nickel alloy
            inventory in the world, with titanium, high grade steel and
            aerospace grade aluminum added daily. Dozens of distributors,
            including names you already buy from, plus surplus material from
            precision machining facilities like yours, priced to sell, always
            with mill cert&apos;s. One search, live prices, side by side,
            instant checkout.{" "}
            <span className="font-semibold text-[#00062e]">
              What used to take a week of calls now takes one screen.
            </span>
          </p>
          <p>
            <span className="font-semibold text-[#00062e]">
              Every order comes with full mill certs, traceable to heat, in
              your hands within 24 hours of purchase.
            </span>{" "}
            Every listing, every time.
          </p>
          <p>
            Take the price and check out in under two minutes, or send an RFQ
            and we will hold the material while your quality team validates
            it. Same inventory, same certs, your call.
          </p>
        </div>

        <figure className="mx-auto mt-10 max-w-3xl">
          <div className="rounded-2xl border-l-4 border-[#009499] bg-white p-8 shadow-sm sm:p-10">
            <blockquote className="text-lg italic leading-relaxed text-[#00062e]">
              &ldquo;I spent decades selling high-performance alloys the old
              way: the phone, the fax, the favor. The material was always out
              there; the time to find it and price it was what cost shops the
              job. Kono puts that whole market on one screen. If it is listed,
              it is real, it is certified, and you can buy it right now. For the
              distributors who list with us, it is the same deal: your
              inventory in front of every machine shop in the country, with
              none of the chasing. That is how metals should have been bought
              all along.&rdquo;
            </blockquote>
            <figcaption className="mt-6 flex items-center gap-4">
              <img
                src="/team/michael-resl-2026-face.jpg"
                alt="Michael Resl"
                className="h-12 w-12 rounded-full object-cover"
              />
              <div>
                <div className="font-bold text-[#00062e]">Michael Resl</div>
                <div className="text-sm text-gray-500">CEO, Kono</div>
              </div>
            </figcaption>
          </div>
        </figure>
      </div>

      {/* CTA */}
      <div className="mx-auto mt-14 flex max-w-3xl flex-wrap justify-center gap-4">
        <Link
          to="/"
          className="rounded-full bg-[#00062e] px-6 py-3 text-sm font-bold text-white hover:bg-slate-900"
        >
          Start searching inventory
        </Link>
        <a
          href={SUPPORT_EMAIL_LINK}
          className="rounded-full border-2 border-[#00062e] px-6 py-3 text-sm font-bold text-[#00062e] hover:bg-slate-100"
        >
          Talk to our team
        </a>
      </div>
    </div>
  );
};

const FAQS: { q: string; a: React.ReactNode }[] = [
  {
    q: "How does free shipping work?",
    a: (
      <>
        Qualifying orders ship free through our freight network, typically
        delivered within about 7 business days. For lighter or time-sensitive
        orders, live carrier rates are shown at checkout — and when a faster
        option is available, you can choose to upgrade to expedited paid
        shipping. The free and paid choices are always shown side by side so you
        can pick what works best for your timeline.
      </>
    ),
  },
  {
    q: "How do I contact Kono?",
    a: (
      <>
        We'd love to hear from you. Call us at{" "}
        <a href={SUPPORT_PHONE_LINK} className="text-[#009499] hover:underline">
          {SUPPORT_PHONE}
        </a>{" "}
        or email{" "}
        <a href={SUPPORT_EMAIL_LINK} className="text-[#009499] hover:underline">
          {SUPPORT_EMAIL}
        </a>
        . Our team responds quickly and is happy to help with pricing,
        specifications, lead times, or anything else.
      </>
    ),
  },
  {
    q: "Where are you located?",
    a: (
      <>
        Kono is located at 21163 Newport Coast Drive, Newport Beach, CA 92657.
        We are open Monday to Friday, 8 am to 5 pm.
      </>
    ),
  },
  {
    q: "Can you sell metals that aren't listed on the Kono platform?",
    a: (
      <>
        Very likely, yes. Our platform focuses on high-performance alloys, but
        our team can source many other metals, grades, and forms that aren't
        shown online. Just{" "}
        <a href={SUPPORT_EMAIL_LINK} className="text-[#009499] hover:underline">
          contact us
        </a>{" "}
        with what you need and we'll do our best to find it for you.
      </>
    ),
  },
  {
    q: "How does Kono use AI?",
    a: (
      <>
        AI is embedded across the platform: it interprets your search in plain
        language, reads and structures uploaded RFQs, helps with pricing, and
        streamlines logistics. The goal is simple — get you the right material,
        at a fair price, with less back-and-forth.
      </>
    ),
  },
];

export const FaqPage = () => {
  return (
    <div className="mx-auto max-w-3xl pb-16">
      <PageHeading>Frequently asked questions</PageHeading>
      <p className="mt-4 text-lg text-gray-600">
        Everything you need to know about buying alloys on Kono. Can't find your
        answer?{" "}
        <a href={SUPPORT_EMAIL_LINK} className="text-[#009499] hover:underline">
          Reach out
        </a>
        .
      </p>

      <dl className="mt-8 divide-y divide-gray-100 rounded-xl border border-gray-100">
        {FAQS.map((item) => (
          <Disclosure as="div" key={item.q} className="px-5">
            {({ open }) => (
              <>
                <dt>
                  <Disclosure.Button className="flex w-full items-center justify-between py-5 text-left">
                    <span className="text-base font-semibold text-[#00062e]">
                      {item.q}
                    </span>
                    <ChevronDownIcon
                      className={`h-5 w-5 shrink-0 text-gray-400 transition ${
                        open ? "rotate-180" : ""
                      }`}
                    />
                  </Disclosure.Button>
                </dt>
                <Disclosure.Panel as="dd" className="pb-5 pr-6 text-gray-600">
                  {item.a}
                </Disclosure.Panel>
              </>
            )}
          </Disclosure>
        ))}
      </dl>
    </div>
  );
};

export const PrivacyPage = () => {
  return (
    <div className="mx-auto max-w-3xl pb-16 text-gray-700">
      <PageHeading>Privacy Policy</PageHeading>
      <p className="mt-3 text-sm text-gray-500">Last updated: July 13, 2026</p>

      <div className="mt-6 space-y-6 leading-relaxed">
        <p>
          This Privacy Policy explains how Kono ("Kono", "we", "us") collects,
          uses, and protects information when you use kono.ai and our related
          services (the "Platform"). By using the Platform, you agree to the
          practices described below.
        </p>

        <Section title="Information we collect">
          <ul className="list-disc space-y-1 pl-6">
            <li>
              <span className="font-semibold">Account information</span> — such
              as your name, email address, company, and contact details when you
              create an account or submit an inquiry.
            </li>
            <li>
              <span className="font-semibold">Activity you create</span> — such
              as searches, uploaded RFQs, saved items, carts, and orders.
            </li>
            <li>
              <span className="font-semibold">Transaction details</span> — order
              and shipping information needed to fulfill purchases. Payment card
              details are processed by our payment provider and are not stored
              by Kono.
            </li>
            <li>
              <span className="font-semibold">Usage and device data</span> —
              such as pages viewed, approximate location, browser type, and
              similar analytics collected automatically.
            </li>
          </ul>
        </Section>

        <Section title="How we use information">
          <ul className="list-disc space-y-1 pl-6">
            <li>Provide, operate, and improve the Platform.</li>
            <li>
              Process quotes, orders, shipping, and customer support requests.
            </li>
            <li>
              Personalize your experience — for example, showing your recent
              searches and uploaded RFQs, and, if you opt in, notifying you
              about price or inventory changes for items you follow.
            </li>
            <li>
              Communicate with you about your account, orders, and service.
            </li>
            <li>Maintain security and prevent fraud or abuse.</li>
          </ul>
        </Section>

        <Section title="How we share information">
          <p>
            We do not sell your personal information. We share information only
            with trusted service providers who help us operate the Platform (for
            example, cloud hosting, payment processing, email delivery, and
            analytics), and only as needed to provide the service. We may also
            disclose information where required by law or to protect our rights,
            users, and the public.
          </p>
        </Section>

        <Section title="Cookies and analytics">
          <p>
            We use cookies and similar technologies grouped into four
            categories:
          </p>
          <ul className="mt-2 list-disc space-y-1 pl-6">
            <li>
              <span className="font-semibold">Strictly necessary</span> — keep
              you signed in and secure and power core features like your cart
              and checkout. These are always on.
            </li>
            <li>
              <span className="font-semibold">Functional</span> — remember your
              preferences and choices, such as recent searches and saved items.
            </li>
            <li>
              <span className="font-semibold">Analytics</span> — help us
              understand how visitors use the Platform so we can improve it.
            </li>
            <li>
              <span className="font-semibold">Marketing</span> — help us tailor
              and measure communications that may be relevant to you.
            </li>
          </ul>
          <p className="mt-3">
            When you first visit, we ask for your consent and you can accept
            all, keep only the strictly necessary, or choose specific
            categories. You can change your choice at any time:
          </p>
          <button
            type="button"
            onClick={() =>
              window.dispatchEvent(
                new CustomEvent("kono-open-cookie-preferences"),
              )
            }
            className="mt-3 rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-800 hover:bg-gray-50"
          >
            Cookie preferences
          </button>
        </Section>

        <Section title="Data retention and security">
          <p>
            We retain information for as long as your account is active or as
            needed to provide the service and meet legal obligations. We use
            reasonable technical and organizational measures to protect your
            information, though no method of transmission or storage is
            completely secure.
          </p>
        </Section>

        <Section title="Your choices and rights">
          <p>
            You can review and update your account information, delete your
            saved searches and uploaded RFQs from within the Platform, and
            request access to or deletion of your personal information by
            contacting us. You may also opt out of non-essential communications
            at any time.
          </p>
        </Section>

        <Section title="Children's privacy">
          <p>
            The Platform is intended for business use and is not directed to
            children under 16. We do not knowingly collect personal information
            from children.
          </p>
        </Section>

        <Section title="Changes to this policy">
          <p>
            We may update this Privacy Policy from time to time. When we do, we
            will revise the "Last updated" date above and, where appropriate,
            provide additional notice.
          </p>
        </Section>

        <Section title="Contact us">
          <p>
            Questions about this policy or your data? Email{" "}
            <a
              href={SUPPORT_EMAIL_LINK}
              className="text-[#009499] hover:underline"
            >
              {SUPPORT_EMAIL}
            </a>{" "}
            or call{" "}
            <a
              href={SUPPORT_PHONE_LINK}
              className="text-[#009499] hover:underline"
            >
              {SUPPORT_PHONE}
            </a>
            . Kono is located at 21163 Newport Coast Drive, Newport Beach, CA
            92657.
          </p>
        </Section>
      </div>
    </div>
  );
};

export const TermsPage = () => {
  return (
    <div className="mx-auto max-w-3xl pb-16 text-gray-700">
      <PageHeading>General Terms and Conditions of Purchase</PageHeading>
      <p className="mt-3 text-sm text-gray-500">
        Effective date: {TERMS_EFFECTIVE_DATE}
      </p>

      <div className="mt-6 space-y-6 leading-relaxed">
        {TERMS_PREAMBLE.map((paragraph) => (
          <p key={paragraph.slice(0, 40)}>{paragraph}</p>
        ))}

        {TERMS_SECTIONS.map((section) => (
          <Section key={section.title} title={section.title}>
            <div className="space-y-4">
              {section.blocks.map((block, index) =>
                "p" in block ? (
                  <p key={index}>{block.p}</p>
                ) : (
                  <ol
                    key={index}
                    className="list-decimal space-y-1 pl-6 marker:text-gray-400"
                  >
                    {block.ol.map((item) => (
                      <li key={item.slice(0, 40)}>{item}</li>
                    ))}
                  </ol>
                ),
              )}
            </div>
          </Section>
        ))}

        <p className="border-t border-gray-100 pt-6 text-sm text-gray-500">
          Questions about these terms? Email{" "}
          <a
            href={SUPPORT_EMAIL_LINK}
            className="text-[#009499] hover:underline"
          >
            {SUPPORT_EMAIL}
          </a>{" "}
          or call{" "}
          <a
            href={SUPPORT_PHONE_LINK}
            className="text-[#009499] hover:underline"
          >
            {SUPPORT_PHONE}
          </a>
          .
        </p>
      </div>
    </div>
  );
};

const Section = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <section>
    <h2 className="mb-2 text-xl font-semibold text-[#00062e]">{title}</h2>
    {children}
  </section>
);
